import { useState } from 'react'

import { useAuth } from '@/auth/AuthProvider'
import { FxAlert, FxButton, FxPanel, FxSpinner } from '@/components/fx'
import { CreateApiKeyDialog } from './CreateApiKeyDialog'
import { RevokeApiKeyDialog } from './RevokeApiKeyDialog'
import { apiDocsUrl } from './apiDocsUrl'
import {
  activeKeyCount,
  apiKeyErrorMessage,
  formatKeyDate,
  formatLastUsed,
  shortKeyId,
  type ApiKey,
} from './apiKeys'
import { useApiKeys } from './useApiKeys'

/**
 * Einstellungen → API-Zugang, for everyone.
 *
 * Not an administrator's screen despite the path it calls: a key belongs to an
 * account, `/v1/admin/api-keys` is open to every authenticated caller and
 * scoped to them, and gating it behind a role would lock people out of their
 * own integrations.
 *
 * Carried over from the retired portal's `admin/pages/ApiKeys`: the listing,
 * creating and revoking. Left behind: its three stat tiles (three numbers to
 * say what a five-row table already says) and its active/inactive switch —
 * `PATCH is_active` is a second, softer kind of off beside Widerrufen, and two
 * ways to disable a key mean nobody can say which one an integration hit. A
 * key that is already inactive is still shown as such, because the endpoint
 * can still return one.
 */
export function ApiAccessSection() {
  const { user } = useAuth()
  const [createOpen, setCreateOpen] = useState(false)
  const [revoking, setRevoking] = useState<ApiKey | null>(null)
  const { data: keys, isLoading, isError, error, refetch } = useApiKeys()

  const rows = keys ?? []

  return (
    <section className="flex flex-col gap-7 md:grid md:grid-cols-[360px_minmax(0,1fr)] md:items-start">
      <FxPanel className="flex flex-col gap-4 p-7">
        <h2 className="m-0 text-panel-title text-text">Zugang über die API</h2>
        <p className="m-0 text-small text-text">
          Ein Schlüssel gehört zu Ihrem Konto und kann alles, was Sie können. Geben Sie ihn nicht
          weiter und legen Sie lieber einen zweiten an, den Sie einzeln widerrufen können.
        </p>
        <a
          href={apiDocsUrl()}
          target="_blank"
          rel="noreferrer"
          className="text-body-strong text-primary-text underline-offset-2 hover:underline"
        >
          Zur API-Dokumentation <span aria-hidden="true">→</span>
        </a>
        <hr className="my-1 border-0 border-t-2 border-divider" />
        <FxButton block icon="key" onClick={() => setCreateOpen(true)}>
          Neuen Schlüssel anlegen
        </FxButton>
        <p className="m-0 text-caption text-text-muted">
          Der Schlüssel wird genau einmal angezeigt. Danach ist er nicht mehr lesbar — auch für uns
          nicht.
        </p>
      </FxPanel>

      <FxPanel
        className="p-7"
        title="Ihre Schlüssel"
        meta={
          // Never a count while the answer is unknown: an empty table under
          // "0 aktiv" would state as fact what a failed request cannot.
          isLoading || isError ? undefined : (
            <span className="text-small text-text-muted">
              {activeKeyCount(rows)} aktiv{rows.length > activeKeyCount(rows) ? ` von ${rows.length}` : ''}
            </span>
          )
        }
      >
        {isLoading ? (
          <FxSpinner label="Schlüssel werden geladen" />
        ) : isError ? (
          <FxAlert tone="danger" title="Die Schlüssel konnten nicht geladen werden">
            <p>{apiKeyErrorMessage(error, 'Bitte versuchen Sie es noch einmal.')}</p>
            <FxButton className="mt-4" variant="outlined" onClick={() => void refetch()}>
              Erneut versuchen
            </FxButton>
          </FxAlert>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-start gap-4">
            <p className="m-0 text-body text-text">
              Sie haben noch keinen Schlüssel. Legen Sie einen an, um Formulare und Ergebnisse aus
              einem anderen Programm heraus abzurufen.
            </p>
            <FxButton icon="key" onClick={() => setCreateOpen(true)}>
              Neuen Schlüssel anlegen
            </FxButton>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-left text-body">
                <caption className="sr-only">Ihre API-Schlüssel</caption>
                <thead>
                  <tr className="text-small text-text-muted">
                    <th scope="col" className="pb-2 pr-4 font-normal">
                      Name
                    </th>
                    <th scope="col" className="pb-2 pr-4 font-normal">
                      Schlüssel
                    </th>
                    <th scope="col" className="pb-2 pr-4 font-normal">
                      Angelegt
                    </th>
                    <th scope="col" className="pb-2 pr-4 font-normal">
                      Zuletzt benutzt
                    </th>
                    <th scope="col" className="pb-2">
                      <span className="sr-only">Aktion</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-divider">
                  {rows.map((key) => {
                    // An administrator's listing is not scoped to them, so a
                    // row can belong to someone else. Saying whose it is turns
                    // a wrong revoke into an avoidable one.
                    const foreign =
                      key.created_by != null &&
                      user?.email != null &&
                      key.created_by.toLowerCase() !== user.email.toLowerCase()
                    return (
                      <tr key={key.id} className="align-middle">
                        <th scope="row" className="py-3 pr-4 text-left text-body-strong font-semibold">
                          {key.name}
                          {!key.is_active && (
                            <span className="ml-2 text-small font-normal text-text-muted">
                              deaktiviert
                            </span>
                          )}
                          {foreign && (
                            <span className="block text-small font-normal text-text-muted">
                              angelegt von {key.created_by}
                            </span>
                          )}
                        </th>
                        <td className="py-3 pr-4 font-mono text-small text-text-muted">
                          {shortKeyId(key.id)}
                        </td>
                        <td className="py-3 pr-4 tabular-nums">{formatKeyDate(key.created_at)}</td>
                        <td
                          className={`py-3 pr-4 tabular-nums ${key.last_used_at ? 'text-text' : 'text-text-muted'}`}
                        >
                          {formatLastUsed(key.last_used_at)}
                        </td>
                        <td className="py-3 text-right">
                          <FxButton
                            variant="dangerTonal"
                            className="h-tab px-3"
                            aria-label={`Schlüssel „${key.name}“ widerrufen`}
                            onClick={() => setRevoking(key)}
                          >
                            Widerrufen
                          </FxButton>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <p className="mt-5 text-caption text-text-muted">
              In der Spalte „Schlüssel“ steht die Kennung des Schlüssels, nicht der Schlüssel selbst
              — den zeigen wir nur einmal beim Anlegen.
            </p>
          </>
        )}
      </FxPanel>

      <CreateApiKeyDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={() => void refetch()}
      />
      <RevokeApiKeyDialog apiKey={revoking} onClose={() => setRevoking(null)} />
    </section>
  )
}
